/**
 * GradientBackground Component
 *
 * Animated gradient backgrounds for templates.
 * Features:
 * - Multiple preset gradient styles
 * - Custom color support
 * - Animated gradient movement
 * - Noise/grain overlay option
 */

import React from 'react';
import { useCurrentFrame, AbsoluteFill } from 'remotion';

export type GradientPreset =
  | 'midnight'      // Dark blue to purple
  | 'sunset'        // Orange to pink
  | 'ocean'         // Teal to blue
  | 'forest'        // Green to dark
  | 'fire'          // Red to orange
  | 'luxury'        // Black to gold accents
  | 'minimal'       // Light gray gradient
  | 'custom';

export interface GradientBackgroundProps {
  /** Preset gradient style */
  preset?: GradientPreset;
  /** Custom colors (for 'custom' preset) */
  customColors?: string[];
  /** Gradient direction in degrees */
  direction?: number;
  /** Enable animated gradient movement */
  animate?: boolean;
  /** Animation speed (lower = slower) */
  animationSpeed?: number;
  /** Add noise/grain overlay */
  enableNoise?: boolean;
  /** Noise opacity (0-1) */
  noiseOpacity?: number;
  /** Children to render on top */
  children?: React.ReactNode;
}

// Preset color definitions
const GRADIENT_PRESETS: Record<GradientPreset, string[]> = {
  midnight: ['#1a1a2e', '#16213e', '#0f3460'],
  sunset: ['#FF416C', '#FF4B2B', '#FF8C00'],
  ocean: ['#4ECDC4', '#44A08D', '#093637'],
  forest: ['#134E5E', '#71B280', '#0F2027'],
  fire: ['#FF416C', '#FF4B2B', '#F7971E'],
  luxury: ['#1a1a1a', '#2d2d2d', '#0d0d0d'],
  minimal: ['#f5f5f5', '#e0e0e0', '#d5d5d5'],
  custom: ['#000000', '#333333'],
};

export const GradientBackground: React.FC<GradientBackgroundProps> = ({
  preset = 'midnight',
  customColors,
  direction = 180,
  animate = false,
  animationSpeed = 0.5,
  enableNoise = false,
  noiseOpacity = 0.03,
  children,
}) => {
  const frame = useCurrentFrame();

  // Get colors based on preset or custom
  const colors = preset === 'custom' && customColors
    ? customColors
    : GRADIENT_PRESETS[preset];

  // Animate direction if enabled
  const animatedDirection = animate
    ? direction + Math.sin(frame * animationSpeed * 0.02) * 30
    : direction;

  // Create gradient string
  const gradientString = `linear-gradient(${animatedDirection}deg, ${colors.join(', ')})`;

  return (
    <AbsoluteFill
      style={{
        background: gradientString,
      }}
    >
      {/* Optional noise overlay */}
      {enableNoise && (
        <AbsoluteFill
          style={{
            opacity: noiseOpacity,
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            backgroundSize: '128px 128px',
            mixBlendMode: 'overlay',
          }}
        />
      )}

      {/* Children content */}
      {children}
    </AbsoluteFill>
  );
};

/**
 * Utility component for animated gradient text
 */
export interface GradientTextProps {
  text: string;
  colors?: string[];
  fontSize?: number;
  fontWeight?: number;
  fontFamily?: string;
  animate?: boolean;
}

export const GradientText: React.FC<GradientTextProps> = ({
  text,
  colors = ['#FF6B35', '#FFD700', '#FF6B35'],
  fontSize = 48,
  fontWeight = 700,
  fontFamily = 'Inter, sans-serif',
  animate = false,
}) => {
  const frame = useCurrentFrame();

  // Animate gradient position
  const backgroundPosition = animate
    ? `${Math.sin(frame * 0.05) * 50 + 50}% center`
    : 'center';

  return (
    <span
      style={{
        fontSize,
        fontWeight,
        fontFamily,
        background: `linear-gradient(90deg, ${colors.join(', ')})`,
        backgroundSize: '200% auto',
        backgroundPosition,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
      }}
    >
      {text}
    </span>
  );
};
